import React, {
    useState,
    useCallback,
    useRef,
    forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import { makeStyles } from '@material-ui/core/styles';
import ContentFilter from '@material-ui/icons/FilterList';
import FilterAddIcon from '../../Icons/FilterAdd';
import classnames from 'classnames';
import lodashGet from 'lodash/get';
import { useHistory } from 'react-router-dom';
import { stringify } from 'query-string';
import {
    Button,
    FieldTitle,
    useTranslate,
    useListContext,
} from 'react-admin';

// import { useSavedQueries, AddFilterSetDialog, RemoveFilterSetDialog } from '@react-admin/ra-preferences';
import useFilterSetsPreferences from '../../../controller/preferences/useFilterSetsPreferences';
import AddFilterSetDialog from './AddFilterSetDialog';
import RemoveFilterSetDialog from './RemoveFilterSetDialog';


const useStyles = makeStyles(theme => ({
    root: { display: 'inline-block' },
    menu: {
        backgroundColor: theme.palette.background.paper,
    },
}), { name: 'RaFilterButton' });

export const FilterButtonWithSave = (props) => {
    const { filters, className, ...rest } = props;
    const {
        filterValues,
        currentSort,
        perPage,
        displayedFilters = {},
        showFilter,
        resource,
    } = useListContext();
    const translate = useTranslate();
    const [open, setOpen] = useState(false);
    const anchorEl = useRef();
    const classes = useStyles(props);
    const [filterSets] = useFilterSetsPreferences(resource, []);
    const history = useHistory();

    const hasFilterValues = !isEqual(filterValues, {});
    const hasSavedCurrentFilterSet = filterSets.some(filterSet =>
        isEqual(filterSet.value, {
            filter: filterValues,
            sort: currentSort,
            perPage,
            displayedFilters,
        })
    );

    const hiddenFilters = filters.filter(
        (filterElement) =>
            !filterElement.props.alwaysOn &&
            !displayedFilters[filterElement.props.source] &&
            typeof lodashGet(filterValues, filterElement.props.source) ===
            'undefined'
    );

    // menu state
    const handleClickButton = useCallback(
        event => {
            // This prevents ghost click.
            event.preventDefault();
            setOpen(true);
            anchorEl.current = event.currentTarget;
        },
        [anchorEl, setOpen]
    );
    const handleRequestClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);
    const handleShow = useCallback(
        ({ source, defaultValue }) => {
            showFilter(source, defaultValue);
            setOpen(false);
        },
        [showFilter, setOpen]
    );

    // add query dialog state
    const [addFilterSetDialogOpen, setAddFilterSetDialogOpen] = useState(
        false
    );
    const hideAddFilterSetDialog = () => {
        setAddFilterSetDialogOpen(false);
    };
    const showAddFilterSetDialog = () => {
        setOpen(false);
        setAddFilterSetDialogOpen(true);
    };

    // remove query dialog state
    const [
        removeFilterSetDialogOpen,
        setRemoveFilterSetDialogOpen,
    ] = useState(false);
    const hideRemoveFilterSetDialog = () => {
        setRemoveFilterSetDialogOpen(false);
    };
    const showRemoveFilterSetDialog = () => {
        setOpen(false);
        setRemoveFilterSetDialogOpen(true);
    };

    return (
        <div className={classnames(classes.root, className)} {...rest}>
            <Button
                className="add-filter"
                label="ra.action.add_filter"
                onClick={handleClickButton}
            >
                <FilterAddIcon />
            </Button>
            <Menu
                open={open}
                anchorEl={anchorEl.current}
                onClose={handleRequestClose}
                MenuListProps={{
                    className: classes.menu,
                    subheader: <li />
                }}
            >
                <ListSubheader>{translate('ra-preferences.saved_queries.label')}</ListSubheader>
                {filterSets.map((filterSet, index) =>
                    isEqual(filterSet.value, {
                        filter: filterValues,
                        sort: currentSort,
                        perPage,
                        displayedFilters,
                    }) ? (
                        <MenuItem
                            onClick={showRemoveFilterSetDialog}
                            key={index}
                        >
                            {translate(
                                'ra-preferences.saved_queries.remove_label_with_name',
                                {
                                    _: 'Remove query "%{name}"',
                                    name: filterSet.label,
                                }
                            )}
                        </MenuItem>
                    ) : (
                        <MenuItem
                            onClick={() => {
                                history.push({
                                    search: stringify({
                                        filter: JSON.stringify(
                                            filterSet.value.filter
                                        ),
                                        sort: filterSet.value.sort.field,
                                        order: filterSet.value.sort.order,
                                        page: 1,
                                        perPage: filterSet.value.perPage,
                                        displayedFilters: JSON.stringify(
                                            filterSet.value.displayedFilters
                                        ),
                                    }),
                                });
                                setOpen(false);
                            }}
                            key={index}
                        >
                            {filterSet.label}
                        </MenuItem>
                    )
                )}
                {hasFilterValues && !hasSavedCurrentFilterSet ? (
                    <MenuItem onClick={showAddFilterSetDialog}>
                        {translate('ra-preferences.saved_queries.new_label', {
                            _: 'Save current query...',
                        })}
                    </MenuItem>
                ) : null}
                {Boolean(hiddenFilters.length) && <ListSubheader>Фильтры</ListSubheader>}
                {hiddenFilters.map((filterElement) => (
                    <FilterButtonMenuItem
                        key={filterElement.props.source}
                        filter={filterElement}
                        onShow={handleShow}
                        resource={resource}
                    />
                ))}
            </Menu>
            <AddFilterSetDialog
                open={addFilterSetDialogOpen}
                onClose={hideAddFilterSetDialog}
            />
            <RemoveFilterSetDialog
                open={removeFilterSetDialogOpen}
                onClose={hideRemoveFilterSetDialog}
            />
        </div>
    );
};

FilterButtonWithSave.propTypes = {
    classes: PropTypes.object,
    className: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.node).isRequired,
};


// FIXME: remove once react-admin exports FilterButtonMenuItem
// eslint-disable-next-line react/display-name
const FilterButtonMenuItem = forwardRef(
    (props, ref) => {
        const { filter, onShow, resource } = props;
        const handleShow = useCallback(() => {
            onShow({
                source: filter.props.source,
                defaultValue: filter.props.defaultValue,
            });
        }, [filter.props.defaultValue, filter.props.source, onShow]);

        return (
            <MenuItem
                className="new-filter-item"
                data-key={filter.props.source}
                data-default-value={filter.props.defaultValue}
                key={filter.props.source}
                onClick={handleShow}
                ref={ref}
            >
                <FieldTitle
                    label={filter.props.label}
                    source={filter.props.source}
                    resource={resource}
                />
            </MenuItem>
        );
    }
);

FilterButtonMenuItem.propTypes = {
    filter: PropTypes.element.isRequired,
    onShow: PropTypes.func.isRequired,
    resource: PropTypes.string.isRequired,
};

export default FilterButtonWithSave